import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Business, ChevronRight, Close, Person } from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import CopyButton from '../../Components/CopyButton';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);

// Validated categorical slots (blue / orange) – identity follows the contact type, never its rank
const SERIES = {
  person: '#2a78d6',
  company: '#eb6834',
};
const TEXT_PRIMARY = '#1e293b';
const TEXT_SECONDARY = '#64748b';
const GRID = '#f1f5f9';
const CHART_HEIGHT = 150;

const panelSx = {
  p: 2,
  height: '100%',
  borderRadius: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #f1f5f9',
};

const tooltipStyle = {
  backgroundColor: TEXT_PRIMARY,
  padding: 10,
  cornerRadius: 6,
  displayColors: false,
  titleFont: { size: 12, weight: '600' },
  bodyFont: { size: 12 },
};

const axisTicks = { color: TEXT_SECONDARY, font: { size: 11 } };

// Click anywhere in a bar's band (not only on the bar itself) to drill in
const clickableInteraction = { mode: 'index', axis: 'xy', intersect: false };

const setPointerCursor = (event, elements) => {
  if (event.native?.target) {
    event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
  }
};

const getContactName = (contact) =>
  contact.contactType === 'company' ? contact.companyName : contact.fullName;

const getContactEmail = (contact) =>
  contact.contactType === 'company' ? contact.companyEmail : contact.email;

// Modal listing the contacts behind the clicked bar / segment
const ContactListDialog = ({ drill, onClose, onViewContact }) => {
  const sorted = drill
    ? [...drill.contacts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : [];

  return (
    <Dialog open={Boolean(drill)} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 700, color: TEXT_PRIMARY, display: 'block' }}>
          {drill?.title}
        </Typography>
        <Typography component="span" variant="body2" sx={{ color: TEXT_SECONDARY }}>
          {sorted.length} contact{sorted.length === 1 ? '' : 's'}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close" sx={{ position: 'absolute', right: 12, top: 12 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {sorted.length === 0 ? (
          <Typography variant="body2" sx={{ color: TEXT_SECONDARY, textAlign: 'center', py: 4 }}>
            No contacts
          </Typography>
        ) : (
          <List disablePadding>
            {sorted.map((contact, index) => {
              const isCompany = contact.contactType === 'company';
              const created = new Date(contact.createdAt);
              return (
                <React.Fragment key={contact.dateTimeStamp || index}>
                  <ListItemButton onClick={() => onViewContact?.(contact)} sx={{ px: 3, py: 1.25 }}>
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        sx={{
                          width: 34,
                          height: 34,
                          bgcolor: `${SERIES[isCompany ? 'company' : 'person']}1a`,
                          color: SERIES[isCompany ? 'company' : 'person'],
                        }}
                      >
                        {isCompany ? <Business sx={{ fontSize: 18 }} /> : <Person sx={{ fontSize: 18 }} />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getContactName(contact) || 'Unnamed contact'}
                      primaryTypographyProps={{ fontWeight: 600, color: TEXT_PRIMARY, fontSize: '0.9rem' }}
                      secondary={[getContactEmail(contact), contact.city].filter(Boolean).join(' · ') || 'No email'}
                      secondaryTypographyProps={{ color: TEXT_SECONDARY, fontSize: '0.8rem', noWrap: true }}
                    />
                    <CopyButton value={getContactEmail(contact)} label="Copy email" size={14} />
                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, ml: 2, whiteSpace: 'nowrap' }}>
                      {isNaN(created)
                        ? ''
                        : created.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </Typography>
                    <ChevronRight sx={{ color: '#cbd5e1', ml: 1 }} />
                  </ListItemButton>
                  {index < sorted.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PanelTitle = ({ title, caption }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
      {title}
    </Typography>
    {caption && (
      <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
        {caption}
      </Typography>
    )}
  </Stack>
);

const EmptyState = () => (
  <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
      No contacts for this month
    </Typography>
  </Box>
);

const ContactCharts = ({ contacts, selectedDate, onViewContact }) => {
  const monthLabel = selectedDate.toLocaleString('en-US', { month: 'short' });
  const [drill, setDrill] = useState(null);

  const openDay = (dayIndex) => {
    const day = dayIndex + 1;
    setDrill({
      title: `Added on ${monthLabel} ${day}, ${selectedDate.getFullYear()}`,
      contacts: contacts.filter((c) => {
        const created = new Date(c.createdAt);
        return (
          created.getFullYear() === selectedDate.getFullYear() &&
          created.getMonth() === selectedDate.getMonth() &&
          created.getDate() === day
        );
      }),
    });
  };

  const openCity = (city) => {
    setDrill({
      title: `Contacts in ${city}`,
      contacts: contacts.filter((c) => (c.city?.trim() || 'Unknown') === city),
    });
  };

  const openType = (type) => {
    setDrill({
      title: type.label,
      contacts: contacts.filter((c) => c.contactType === type.key),
    });
  };

  const viewContact = (contact) => {
    setDrill(null);
    onViewContact?.(contact);
  };

  // Contacts added on each day of the selected month
  const daily = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const counts = Array(days).fill(0);
    contacts.forEach((contact) => {
      const created = new Date(contact.createdAt);
      if (created.getFullYear() === year && created.getMonth() === month) {
        counts[created.getDate() - 1]++;
      }
    });
    return counts;
  }, [contacts, selectedDate]);

  const topCities = useMemo(() => {
    const counts = contacts.reduce((acc, contact) => {
      const city = contact.city?.trim() || 'Unknown';
      acc[city] = (acc[city] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [contacts]);

  const typeSplit = useMemo(() => {
    const person = contacts.filter((c) => c.contactType === 'person').length;
    const company = contacts.filter((c) => c.contactType === 'company').length;
    return [
      { key: 'person', label: 'Individuals', count: person },
      { key: 'company', label: 'Companies', count: company },
    ];
  }, [contacts]);

  const typeTotal = typeSplit.reduce((sum, t) => sum + t.count, 0);
  const peakDay = Math.max(...daily);
  // Average over the days that have happened so far (all days for a past month)
  const now = new Date();
  const isCurrentMonth =
    selectedDate.getMonth() === now.getMonth() && selectedDate.getFullYear() === now.getFullYear();
  const elapsedDays = isCurrentMonth ? now.getDate() : daily.length;
  const avgPerDay = Math.round((daily.slice(0, elapsedDays).reduce((a, b) => a + b, 0) / elapsedDays) * 10) / 10;

  const dailyData = {
    labels: daily.map((_, i) => i + 1),
    datasets: [
      {
        data: daily,
        backgroundColor: SERIES.person,
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: 'bottom',
        maxBarThickness: 24,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
      },
    ],
  };

  const dailyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: clickableInteraction,
    onHover: (event, elements) => setPointerCursor(event, elements.filter((el) => daily[el.index] > 0)),
    onClick: (event, elements) => {
      if (elements.length && daily[elements[0].index] > 0) openDay(elements[0].index);
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          title: (items) => `${monthLabel} ${items[0].label}`,
          label: (item) => `${item.raw} contact${item.raw === 1 ? '' : 's'} added`,
          footer: (items) => (items[0]?.raw > 0 ? 'Click to view contacts' : ''),
        },
        footerFont: { size: 11, weight: '400' },
        footerColor: '#cbd5e1',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        border: { display: false },
        ticks: { ...axisTicks, precision: 0, maxTicksLimit: 4 },
        grid: { color: GRID },
      },
      x: {
        border: { color: '#e2e8f0' },
        ticks: { ...axisTicks, autoSkip: true, maxRotation: 0, maxTicksLimit: 10 },
        grid: { display: false },
      },
    },
  };

  const cityData = {
    labels: topCities.map(([city]) => city),
    datasets: [
      {
        data: topCities.map(([, count]) => count),
        backgroundColor: SERIES.person,
        borderRadius: { topRight: 4, bottomRight: 4 },
        borderSkipped: 'left',
        maxBarThickness: 18,
      },
    ],
  };

  const cityOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    interaction: clickableInteraction,
    onHover: setPointerCursor,
    onClick: (event, elements) => {
      if (elements.length) openCity(topCities[elements[0].index][0]);
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          label: (item) => `${item.raw} contact${item.raw === 1 ? '' : 's'}`,
          footer: () => 'Click to view contacts',
        },
        footerFont: { size: 11, weight: '400' },
        footerColor: '#cbd5e1',
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        border: { display: false },
        ticks: { ...axisTicks, precision: 0, maxTicksLimit: 4 },
        grid: { color: GRID },
      },
      y: {
        border: { color: '#e2e8f0' },
        ticks: axisTicks,
        grid: { display: false },
      },
    },
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={panelSx}>
            <PanelTitle
              title="Contacts added per day"
              caption={typeTotal ? `Avg ${avgPerDay}/day · Peak ${peakDay}` : null}
            />
            {typeTotal ? (
              <Box sx={{ height: CHART_HEIGHT }}>
                <Bar data={dailyData} options={dailyOptions} aria-label="Contacts added per day this month" />
              </Box>
            ) : (
              <EmptyState />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={panelSx}>
            <PanelTitle title="Top cities" />
            {topCities.length ? (
              <Box sx={{ height: CHART_HEIGHT }}>
                <Bar data={cityData} options={cityOptions} aria-label="Top cities by contact count" />
              </Box>
            ) : (
              <EmptyState />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={panelSx}>
            <PanelTitle title="Contact type" caption={`${typeTotal} total`} />
            {typeTotal ? (
              <Stack spacing={2} sx={{ height: CHART_HEIGHT, justifyContent: 'center' }}>
                {/* Split bar: segments separated by a 2px surface gap */}
                <Box sx={{ display: 'flex', gap: '2px', height: 20 }} role="img" aria-label="Contact type split">
                  {typeSplit
                    .filter((t) => t.count > 0)
                    .map((t, i, shown) => (
                      <Tooltip key={t.key} title={`${t.label}: ${t.count} · click to view`} arrow>
                        <Box
                          onClick={() => openType(t)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.85 },
                            flex: t.count,
                            bgcolor: SERIES[t.key],
                            borderTopLeftRadius: i === 0 ? 4 : 0,
                            borderBottomLeftRadius: i === 0 ? 4 : 0,
                            borderTopRightRadius: i === shown.length - 1 ? 4 : 0,
                            borderBottomRightRadius: i === shown.length - 1 ? 4 : 0,
                          }}
                        />
                      </Tooltip>
                    ))}
                </Box>
                {/* Legend with direct values */}
                <Stack spacing={1}>
                  {typeSplit.map((t) => (
                    <Stack
                      key={t.key}
                      component={ButtonBase}
                      onClick={() => openType(t)}
                      disabled={t.count === 0}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ width: '100%', justifyContent: 'flex-start', px: 0.5, py: 0.25, borderRadius: 1, '&:hover': { bgcolor: '#f8fafc' } }}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: SERIES[t.key] }} />
                      <Typography variant="body2" sx={{ color: TEXT_SECONDARY, flex: 1, textAlign: 'left' }}>
                        {t.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 700 }}>
                        {t.count}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TEXT_SECONDARY, width: 36, textAlign: 'right' }}>
                        {Math.round((t.count / typeTotal) * 100)}%
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ) : (
              <EmptyState />
            )}
          </Paper>
        </Grid>
      </Grid>
      <ContactListDialog drill={drill} onClose={() => setDrill(null)} onViewContact={viewContact} />
    </>
  );
};

export default ContactCharts;
